import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AddonMode } from '@src/slide/domain/ui-model/element-modeles/property/addon/addon.interface';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { enumerateGroupItems } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { ImageTypes } from '@src/slide/infrastructure/module/dependent/image/constant/image-type';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { AttachmentClassify, EShowType } from '../element-modeles/property/attachment/constants';
import { DataType, IDrawingShapeDataModel } from '@tencent/slide-sdk-model';
import type { Nullable } from '@src/slide/infrastructure/module/independent/types';
import type { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { IAttachmentShapeModel, IIframeShapeModel, IIframeTextModeShapeModel, IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import type { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import type { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import type { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import type { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import type { IEmptyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/empty-model.interface';
import type { IGapModel } from '@src/slide/domain/ui-model/element-modeles/Interface/gap-model.interface';
import type { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import type { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import type { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import type { IAttachmentPictureModel, IAudioPictureModel, IGifPictureModel, IPictureModel, IVideoPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import type { ISectionModel } from '@src/slide/domain/ui-model/element-modeles/Interface/section-model.interface';
import type { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import type { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import type { IThemeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import { browser } from '@utils/agent';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';

export function isShapeModel(model: Nullable<IBaseModel>): model is IShapeModel {
    return model?.type === UIModelType.SHAPE;
}

export function isGroupModel(model: Nullable<IBaseModel>): model is IGroupModel {
    return model?.type === UIModelType.GROUP;
}

// 判断是否是一个元素Model
export function isElementModel(model: IBaseModel): boolean {
    const validTypes = [
        UIModelType.SHAPE,
        UIModelType.GROUP,
        UIModelType.IMAGE,
        UIModelType.CONNECTOR,
        UIModelType.CHART,
        UIModelType.TABLE,
    ];
    return validTypes.includes(model?.type);
}

export function isTableModel(model: Nullable<IBaseModel>): model is ITableModel {
    return model?.type === UIModelType.TABLE;
}

export function isNoteModel(model: Nullable<IBaseModel>): model is INoteModel {
    return model?.type === UIModelType.NOTE;
}

export function isAnimationModel(model: Nullable<IBaseModel>): model is IAnimModel {
    return model?.type === UIModelType.ANIMATION;
}

export function isConnectorModel(model: Nullable<IBaseModel>): model is IConnectorModel {
    return model?.type === UIModelType.CONNECTOR;
}

export function isEmptyModel(model: Nullable<IBaseModel>): model is IEmptyModel {
    return model?.type === UIModelType.EMPTY;
}

export function isGapModel(model: Nullable<IBaseModel>): model is IGapModel {
    return model?.type === UIModelType.GAP;
}

export function isLayoutModel(model: Nullable<IBaseModel>): model is ILayoutModel {
    return model?.type === UIModelType.LAYOUT;
}

export function isMasterModel(model: Nullable<IBaseModel>): model is IMasterModel {
    return model?.type === UIModelType.LAYOUT;
}

export function isMultiplyModel(model: Nullable<IBaseModel>): model is IMultiplyModel {
    return model?.type === UIModelType.MULTIPLY;
}

export function isPictureModel(model: Nullable<IBaseModel>): model is IPictureModel {
    return model?.type === UIModelType.IMAGE;
}

export function is3DModel(model: Nullable<IBaseModel>): model is IElementBaseModel {
    return (isShapeModel(model) || isPictureModel(model))
        && (!isEmpty(model.scene3d) || !isEmpty(model.shape3d));
}

export function isPresentationModel(model: Nullable<IBaseModel>): model is IPresentationModel {
    return model?.type === UIModelType.PRESENTATION;
}

export function isSectionModel(model: Nullable<IBaseModel>): model is ISectionModel {
    return model?.type === UIModelType.SECTION;
}

export function isSlideModel(model: Nullable<IBaseModel>): model is ISlideModel {
    return model?.type === UIModelType.SLIDE;
}

export function isThemeModel(model: Nullable<IBaseModel>): model is IThemeModel {
    return model?.type === UIModelType.THEME;
}

export function isIframeShapeModel(uiModel: Nullable<IBaseModel>): uiModel is IIframeShapeModel {
    return isShapeModel(uiModel) && uiModel?.props?.addon?.displayMode === AddonMode.IFRAME;
}

export function isIframeShapeModelInTextMode(uiModel: Nullable<IBaseModel>): uiModel is IIframeTextModeShapeModel {
    return isShapeModel(uiModel)
        && uiModel?.props?.addon?.displayMode === AddonMode.NORMAL
        && !isEmpty(uiModel?.props?.addon?.iframeId);
}

/**
 * 备忘：
 * 并不是所有的附件都有 props.attachment 属性值，attachment 是 slide 自己加的字段
 * 文字链状态和文档附件的都是用 shape-model 承载的，用 attachment 来表示附件
 * 视频预览态和音频预览态在 office ppt 中就是图片加了对应的 video 或 audio 属性，所以没有 attachment 属性
 * 图片附件是 slide 自己加的，office ppt 没图片附件，slide 用 attachment 字段来区分是图片附件还是图片
 */

// 这个函数命名不太对，这里只是判断是否是文字链状态的附件或文档附件
export function isAttachmentShapeModel(uiModel: Nullable<IBaseModel>): uiModel is IAttachmentShapeModel {
    return isShapeModel(uiModel) && !isUndefined(uiModel?.props?.attachment);
}

export function isAttachmentImageElement(uiModel: Nullable<IBaseModel>): uiModel is IAttachmentPictureModel {
    return isPictureModel(uiModel) && !isUndefined(uiModel?.props.attachment);
}

export function isAttachmentModel(uiModel: Nullable<IBaseModel>) {
    return isAttachmentShapeModel(uiModel) || isAttachmentImageElement(uiModel);
}

export function isVideoElement(uiModel: Nullable<IBaseModel>): uiModel is IVideoPictureModel {
    return isPictureModel(uiModel) && !isUndefined(uiModel?.video);
}

// 这个只是预览态的音频
export function isAudioElement(uiModel: Nullable<IBaseModel>): uiModel is IAudioPictureModel {
    return isPictureModel(uiModel) && !isUndefined(uiModel?.audio);
}

// 判断是否是音频附件，包括各种形态的
export function isAudioAttachmentElement(uiModel: Nullable<IBaseModel>): boolean {
    return isAudioElement(uiModel) || isTextLinkOrCardAudio(uiModel);
}

// 判断是否是文字链或卡片态的音频
export function isTextLinkOrCardAudio(uiModel: Nullable<IBaseModel>): boolean {
    return isAttachmentShapeModel(uiModel) && uiModel?.props.attachment?.classify === AttachmentClassify.AUDIO;
}

export function isUnknownAttachmentElement(uiModel: Nullable<IBaseModel>): uiModel is IPictureModel {
    return isAttachmentShapeModel(uiModel) && [undefined, 'default'].includes(uiModel?.props.attachment?.classify);
}

export function isDocAttachmentElement(uiModel: Nullable<IBaseModel>): uiModel is IShapeModel {
    return isShapeModel(uiModel) && uiModel?.props.attachment?.classify === AttachmentClassify.DOC;
}

export function isImageAttachmentElement(uiModel: Nullable<IBaseModel>): uiModel is IPictureModel {
    return isPictureModel(uiModel) && uiModel?.props.attachment?.classify === AttachmentClassify.IMAGE;
}

// 改个名，代表是否是dom gif，目前safari、移动端、本地编辑暂时用dom gif，解码问题和内存问题完全解决后开始切换
export function isDomGifPictureModel(uiModel: Nullable<IBaseModel>): uiModel is IGifPictureModel {
    return isPictureModel(uiModel) && uiModel?.props?.imageType === ImageTypes.GIF
        && (!window.ImageDecoder
            || browser.isSafari
            || browser.isMobile
            || !!SlideDataServices.ClientVarsService()?.isLocalEdit());
}

export function isMediaElement(uiModel: IBaseModel): boolean {
    if (_get(uiModel, 'type') === UIModelType.IMAGE) {
        if ((uiModel as IPictureModel)?.video || (uiModel as IPictureModel)?.audio) {
            return true;
        }
    }
    return false;
}

export function isMediaContent(uiModel: IBaseModel): boolean {
    if (uiModel?.type === UIModelType.GROUP) {
        const childModelArr: IBaseModel[] = [];
        enumerateGroupItems(uiModel as IGroupModel, (childModel) => {
            childModelArr.push(childModel);
        }, [UIModelType.GROUP, UIModelType.SHAPE, UIModelType.TABLE, UIModelType.CONNECTOR]);
        return childModelArr.some(e => isMediaElement(e));
    }
    return isMediaElement(uiModel);
}

/**
 * 判断 sdk 数据是否是附件
 * @param sdkData
 * @returns
 */
// eslint-disable-next-line complexity
export function isSdkModelIsAttachment(sdkData) {
    const sdkDataModel = sdkData as IDrawingShapeDataModel;
    return (
        // todo  muzzy 待sdk支持
        // 所有文字链类型或文档类型
        (sdkDataModel.type === DataType.Shape && sdkDataModel.props.nvPr?.attachment)
        // 音视频附件
        || (sdkDataModel.type === DataType.Picture && (sdkDataModel.props.audio || sdkDataModel.props.video))
        // 图片附件
        || (sdkDataModel.type === DataType.Picture && sdkDataModel.props.nvPr?.attachment)
    );
}

/**
 * 判断 uiModel 是否是附件
 * @param uiModel
 * @returns
 */
export function isUiModelIsAttachment(uiModel: IElementBaseModel) {
    return (
        // 所有文字链类型或文档类型
        isAttachmentShapeModel(uiModel)
        // 音视频附件
        || isVideoElement(uiModel) || isAudioElement(uiModel)
        // 图片附件
        || isImageAttachmentElement(uiModel)
    );
}

/**
 * 判断是否是文字链形式的图片或视频
 * 这个状态下不显示业务的浮动工具栏，显示附件提供的预览小窗工具栏
 * @param uiModel
 * @returns
 */
export function isTextLinkImageOrVideo(uiModel: IElementBaseModel) {
    if (isShapeModel(uiModel) && uiModel.props.attachment) {
        const { showType, classify } = uiModel.props.attachment;
        return (showType === EShowType.TEXT || showType === EShowType.SIMPLE_TEXT)
        && (classify === AttachmentClassify.IMAGE || classify === AttachmentClassify.VIDEO);
    }
}

export function isDomCompatibleElement(uiModel: IElementBaseModel) {
    return (
        isVideoElement(uiModel)
        || isAudioElement(uiModel)
        || isDomGifPictureModel(uiModel)
        || isAttachmentShapeModel(uiModel)
        || isAttachmentImageElement(uiModel)
        || isIframeShapeModel(uiModel)
    );
}

export function isGroupHasDomCompatibleElement(groupModel?: IGroupModel): boolean {
    return !!groupModel?.children.some(model => (
        isGroupModel(model) ? isGroupHasDomCompatibleElement(model) : isDomCompatibleElement(model)
    ));
}

/**
 * 判断是否是图片但不是附件
 * @param uiModel
 * @returns
 */
export function isImageButNotAttachment(uiModel: IElementBaseModel) {
    return uiModel.type === UIModelType.IMAGE && !isImageAttachmentElement(uiModel);
}

export function isImageFillShape(uiModel: IElementBaseModel) {
    return uiModel.type === UIModelType.SHAPE && uiModel.props.style.fillBackground?.image;
}

/**
 * 判断是否是图片或者图片填充的形状（附件图片、音视频会被排除）
 * @param model
 * @returns
 */
export function isImageOrImageFillShape(model: IElementBaseModel): boolean {
    return !!((isImageButNotAttachment(model) && !isMediaElement(model)) || isImageFillShape(model));
}

export function isPureShape(model: Nullable<IBaseModel>) {
    return isShapeModel(model) && !isUiModelIsAttachment(model) && !model?.isAddon;
}

export function isShapeOrConnector(model: Nullable<IBaseModel>) {
    return isPureShape(model) || model?.type === UIModelType.CONNECTOR;
}
